// components/auth/AuthenticationManager.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import config from '../../config';
import logger from '../../utils/logging/logger';
import sessionManager from '../../utils/session/sessionManager';
import { authService as internalAuthService } from '../../utils/auth/authService'; // Import authService but keep it internal to the component
import { UserInfo, UNKNOWN_USER_ID } from '../../utils/auth/authTypes';
import { triggerMismatchCheck } from '../../utils/mismatchAlert';
import { isProduction } from '../../utils/environment';

const componentName = 'AuthenticationManager';

// Default user info - copied from existing AuthProvider
const defaultUser: UserInfo = {
    userId: 0,
    userName: '',
    email: '',
    isAnonymous: true,
    firstName: '',
    lastName: '',
    lastLogin: null,
    userName_DisplayMenu: 'Guest',
    userName_DisplayLogin: '',
    // UserType defaults
    userTypeId: 0,
    isAdmin: false,
    isReseller: false,
    isRestricted: false,
    userTypeCode: 'ANON_USER',
    userTypeDescription: 'Anonymous User'
};

interface AuthManagerContextType {
    // User state data
    user: UserInfo;
    isAuthenticated: boolean;
    isAnonymous: boolean;
    isInitializing: boolean;
    error: string | null;
    requiresRelogin: boolean;
    lostUserInfo: UserInfo | null;

    // Authentication actions
    login: (userNameOrEmail: string, password: string) => Promise<UserInfo>;
    logout: () => void;
    convertAnonymousAccount: (
        userId: number,
        newUserName: string,
        email: string,
        firstName: string,
        lastName: string,
        password: string,
        phone?: string,
        city?: string,
        state?: string,
        zipCode?: string,
        country?: string,
        address1?: string,
        address2?: string
    ) => Promise<boolean>;
    checkUsernameAvailability: (userName: string, userId: number) => Promise<number>;
    refreshUserInfo: () => Promise<void>;
    clearReloginFlag: () => void;

    // Additional methods for components
    createAnonymousAccount: () => Promise<UserInfo>;
    saveUserInfoToCookie: (userInfo: Partial<UserInfo>, forceSave?: boolean) => UserInfo;
    ensureUser: () => Promise<UserInfo>;

    // Request state
    lastRefreshTimestamp: number;
    refreshCount: number;
    authVersion: number;
}


//// Define the context type for our Authentication Manager
//interface AuthManagerContextType {
//    // User state data
//    user: UserInfo;
//    isAuthenticated: boolean;
//    isAnonymous: boolean;
//    isInitializing: boolean;
//    error: string | null;
//    requiresRelogin: boolean;
//    lostUserInfo: UserInfo | null;

//    // Authentication actions
//    login: (userNameOrEmail: string, password: string) => Promise<void>;

//    logout: () => void;

//    convertAnonymousAccount: (
//        userId: number,
//        newUserName: string,
//        email: string,
//        firstName: string,
//        lastName: string,
//        password: string
//    ) => Promise<boolean>;
//    checkUsernameAvailability: (userName: string, userId: number) => Promise<number>;
//    refreshUserInfo: () => Promise<void>;
//    clearReloginFlag: () => void;

//    // Request state
//    lastRefreshTimestamp: number;
//    refreshCount: number;
//}


// Create the context with default values
const AuthManagerContext = createContext<AuthManagerContextType>({
    user: defaultUser,
    isAuthenticated: false,
    isAnonymous: true,
    isInitializing: true,
    error: null,
    requiresRelogin: false,
    lostUserInfo: null,
    login: async () => defaultUser,
    logout: () => { },
    convertAnonymousAccount: async () => false,
    checkUsernameAvailability: async () => 0,
    refreshUserInfo: async () => { },
    clearReloginFlag: () => { },
    createAnonymousAccount: async () => defaultUser,
    saveUserInfoToCookie: () => defaultUser,
    ensureUser: async () => defaultUser,
    lastRefreshTimestamp: 0,
    refreshCount: 0,
    authVersion: 0
});

//// Create the context with default values
//const AuthManagerContext = createContext<AuthManagerContextType>({
//    user: defaultUser,
//    isAuthenticated: false,
//    isAnonymous: true,
//    isInitializing: true,
//    error: null,
//    requiresRelogin: false,
//    lostUserInfo: null,
//    login: async () => { },
//    logout: () => { },
//    convertAnonymousAccount: async () => false,
//    checkUsernameAvailability: async () => 0,
//    refreshUserInfo: async () => { },
//    clearReloginFlag: () => { },
//    lastRefreshTimestamp: 0,
//    refreshCount: 0
//});

/**
 * Debounce a function call
 * @param func The function to debounce
 * @param wait The wait time in milliseconds
 */
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;

    return function (...args: Parameters<T>) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
            timeout = null;
            func(...args);
        }, wait);
    };
}

/**
 * AuthenticationManager props
 */
interface AuthenticationManagerProps {
    children: React.ReactNode;
    refreshInterval?: number; // Optional refresh interval in ms
}

/**
 * AuthenticationManager component - centralized manager for user authentication state
 * 
 * This component serves as the single source of truth for user authentication state.
 * It manages all authentication-related API calls, caching, and state updates.
 */
export const AuthenticationManager: React.FC<AuthenticationManagerProps> = ({
    children,
    refreshInterval = 5 * 60 * 1000 // Default to 5 minutes
}) => {
    // State variables - leveraged from existing AuthProvider
    const [user, setUser] = useState<UserInfo>(defaultUser);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAnonymous, setIsAnonymous] = useState(true);
    const [isInitializing, setIsInitializing] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [requiresRelogin, setRequiresRelogin] = useState(false);
    const [lostUserInfo, setLostUserInfo] = useState<UserInfo | null>(null);

    // Add state for tracking refresh operations
    const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState<number>(0);
    const [refreshCount, setRefreshCount] = useState<number>(0);

    //Hard reset on logon (VERY AGRESSIVE!!!!!!!!!!)
    const [authVersion, setAuthVersion] = useState(0);

    // Add refs for tracking authentication promise
    const authPromiseRef = useRef<{
        promise: Promise<UserInfo> | null;
        timestamp: number;
    }>({
        promise: null,
        timestamp: 0
    });

    // Constants for caching and timeouts
    const AUTH_PROMISE_TIMEOUT = 10000; // 10 seconds
    const REFRESH_COOLDOWN = 2000; // 2 seconds - minimum time between refreshes

    // Private method to save user info, replacing direct authService calls
    const saveUserInfo = useCallback((userInfo: UserInfo, forceSave: boolean = false): UserInfo => {
        // Use internal service but keep the logic encapsulated
        return internalAuthService.saveUserInfoToCookie(userInfo, forceSave);
    }, []);

    //Save user info to cookies - centralized method that replaces direct authService calls
    const saveUserInfoToCookie = useCallback((userInfo: Partial<UserInfo>, forceSave: boolean = false): UserInfo => {
        return internalAuthService.saveUserInfoToCookie(userInfo, forceSave);
    }, []);

    // Private method to get current user, replacing direct authService calls
    const getCurrentUser = useCallback((): UserInfo => {
        return internalAuthService.getCurrentUserFromCookie();
    }, []);

    /**
 * Update user state with the provided user info
 */
    //TODO This is getting called twice on anon creation
    const updateUserState = useCallback((userInfo: UserInfo) => {
        // Set the user object
        setUser(userInfo);

        // Update authentication status
        setIsAuthenticated(!!userInfo.userId && !userInfo.isAnonymous);
        setIsAnonymous(userInfo.isAnonymous);

        // Force version increment to trigger re-renders. This pairs with the Agressive const at the top
        setAuthVersion(prev => prev + 1);

        // Log user state update
        logger.log('LogAuth', componentName, 'updateUserState - Updated user state:', {
            userId: userInfo.userId,
            userName: userInfo.userName,
            isAuthenticated: !!userInfo.userId && !userInfo.isAnonymous,
            isAnonymous: userInfo.isAnonymous
        });
    }, []);

    /**
 * Initialize the authentication state
 * Centralized implementation to prevent multiple calls
 */
    const initializeAuth = useCallback(async (): Promise<UserInfo> => {
        // Check if we already have a valid auth promise
        const now = Date.now();
        const existingPromise = authPromiseRef.current.promise;
        const promiseAge = now - authPromiseRef.current.timestamp;

        if (existingPromise && promiseAge < AUTH_PROMISE_TIMEOUT) {
            logger.log('LogAuth', componentName, 'Using existing auth promise', { userId: UNKNOWN_USER_ID });

            return existingPromise;
        }

        // Create a new auth promise
        logger.log('LogAuth', componentName, 'Creating new auth promise', { userId: UNKNOWN_USER_ID });


        setIsInitializing(true);

        const newPromise = (async () => {
            try {
                // Call authService.ensureUser (it now handles all the logic)
                const userInfo = await internalAuthService.ensureUser();

                // Handle requiresLogin flag if present (this is AuthManager-specific)
                if (userInfo && typeof userInfo === 'object' && 'requiresLogin' in userInfo) {
                    const loginRequiredInfo = userInfo as UserInfo & { requiresLogin: boolean };
                    if (loginRequiredInfo.requiresLogin) {
                        setRequiresRelogin(true);
                        // Retrieve lost user info from session storage
                        const storedLostUser = sessionManager.retrieveData<UserInfo>('lost_user_info');
                        if (storedLostUser) {
                            setLostUserInfo(storedLostUser);
                        }

                        // Return cleaned userInfo
                        const { requiresLogin, ...cleanUserInfo } = loginRequiredInfo;
                        updateUserState(cleanUserInfo);
                        return cleanUserInfo;
                    }
                }

                // Update state with user info
                updateUserState(userInfo);
                return userInfo;

            } catch (error) {
                // Graceful fallback
                logger.log('LogError', componentName, 'Auth initialization error:', { userId: UNKNOWN_USER_ID, error });

                const fallbackUser = await internalAuthService.ensureUser();
                updateUserState(fallbackUser);
                return fallbackUser;
            } finally {
                setIsInitializing(false);

                // Clear the promise reference after a timeout
                setTimeout(() => {
                    if (authPromiseRef.current.timestamp === now) {
                        authPromiseRef.current.promise = null;
                    }
                }, AUTH_PROMISE_TIMEOUT);
            }
        })();

        // Store the promise
        authPromiseRef.current = {
            promise: newPromise,
            timestamp: now
        };

        return newPromise;
    }, [updateUserState]);


    /**
     * Debounced version of refreshUserInfo
     * Prevents rapid successive calls
     */
    const debouncedRefreshUserInfo = useCallback(
        debounce(async () => {
            try {
                const now = Date.now();

                // Skip if we've refreshed recently
                if (now - lastRefreshTimestamp < REFRESH_COOLDOWN) {
                    logger.log('LogAuth', componentName, 'Skipping refresh (cooldown)', { userId: user.userId || UNKNOWN_USER_ID });

                    return;
                }

                logger.log('LogAuth', componentName, 'Refreshing user info', { userId: user.userId || UNKNOWN_USER_ID });

                // Clear the timestamp to force a fresh user check
                sessionManager.clearData(config.LAST_ENSURE_USER_KEY);

                // Get fresh user info
                const userInfo = await internalAuthService.ensureUser();

                // Process the user info
                if (userInfo && typeof userInfo === 'object' && 'requiresLogin' in userInfo) {
                    const loginRequiredInfo = userInfo as UserInfo & { requiresLogin: boolean };
                    if (loginRequiredInfo.requiresLogin) {
                        setRequiresRelogin(true);
                        const storedLostUser = sessionManager.retrieveData<UserInfo>('lost_user_info');
                        if (storedLostUser) {
                            setLostUserInfo(storedLostUser);
                        }
                    } else {
                        // Update state with user info
                        updateUserState(userInfo);
                    }
                } else {
                    // Update state with user info
                    updateUserState(userInfo);
                }

                // Update refresh timestamp
                setLastRefreshTimestamp(now);
                setRefreshCount(prev => prev + 1);

                
                logger.log('LogAuth', componentName, 'User info refreshed successfully', { userId: user.userId || UNKNOWN_USER_ID });
            } catch (error) {
                logger.log('LogError', componentName, 'Error refreshing user info:', { userId: UNKNOWN_USER_ID, error });
                setError(error instanceof Error ? error.message : 'Failed to refresh user info');
            }
        }, 500), // 500ms debounce
        [lastRefreshTimestamp, updateUserState]
    );

    /**
     * Non-debounced version of refreshUserInfo for critical operations
     * where an immediate refresh is required
     */
    const refreshUserInfo = useCallback(async () => {
        try {
            const now = Date.now();
            logger.log('LogAuth', componentName, 'Immediate refresh of user info requested', { userId: user.userId || UNKNOWN_USER_ID });

            // Clear the timestamp to force a fresh user check
            sessionManager.clearData(config.LAST_ENSURE_USER_KEY);

            // Get fresh user info
            const userInfo = await internalAuthService.ensureUser();

            // Process the user info
            if (userInfo && typeof userInfo === 'object' && 'requiresLogin' in userInfo) {
                const loginRequiredInfo = userInfo as UserInfo & { requiresLogin: boolean };
                if (loginRequiredInfo.requiresLogin) {
                    setRequiresRelogin(true);
                    const storedLostUser = sessionManager.retrieveData<UserInfo>('lost_user_info');
                    if (storedLostUser) {
                        setLostUserInfo(storedLostUser);
                    }
                } else {
                    // Update state with user info
                    updateUserState(userInfo);
                }
            } else {
                // Update state with user info
                updateUserState(userInfo);
            }

            // Update refresh timestamp
            setLastRefreshTimestamp(now);
            setRefreshCount(prev => prev + 1);

            logger.log('LogAuth', componentName, 'User info refreshed successfully (immediate)', { userId: user.userId || UNKNOWN_USER_ID });
        } catch (error) {
            logger.log('LogError', componentName, 'Error refreshing user info (immediate):', { userId: UNKNOWN_USER_ID, error });
            setError(error instanceof Error ? error.message : 'Failed to refresh user info');
        }
    }, [updateUserState]);

    //Wrapper for authService.ensureUser - ensures a user exists and returns their information
    const ensureUser = useCallback(async (): Promise<UserInfo> => {
        try {
            return await internalAuthService.ensureUser();
        } catch (error) {
            logger.log('LogError', componentName, 'ensureUser failed:', { userId: UNKNOWN_USER_ID, error });
            throw error;
        }
    }, []);

    /**
     * Login function - leveraged from existing AuthProvider
     */
    const login = useCallback(async (userNameOrEmail: string, password: string): Promise<UserInfo> => {
        setIsInitializing(true);
        setError(null);

        try {
            const loggedInUser = await internalAuthService.login(userNameOrEmail, password);
            updateUserState(loggedInUser);
            setRequiresRelogin(false);
            setLostUserInfo(null);

            logger.log('LogAuth', componentName, 'User logged in successfully:', {
                userId: loggedInUser.userId,
                user: loggedInUser
            });

            // Trigger mismatch check for admins (or always in dev/staging)
            if (!isProduction() || (loggedInUser.userTypeId ?? 0) >= 1000) {
                triggerMismatchCheck();
            }
            
            return loggedInUser;
        } catch (error) {
            logger.log('LogError', componentName, 'Login error:', { userId: UNKNOWN_USER_ID, error });
            setError(error instanceof Error ? error.message : 'Login failed');
            throw error;
        } finally {
            setIsInitializing(false);
        }
    }, [updateUserState]);

    ///**
    // * Logout function - leveraged from existing AuthProvider
    // */
    //const logout = useCallback(() => {
    //    // Call the internalAuthService logout
    //    internalAuthService.logout();

    //    // Reset state to default values
    //    setUser(defaultUser);
    //    setIsAuthenticated(false);
    //    setIsAnonymous(true);
    //    setRequiresRelogin(false);
    //    setLostUserInfo(null);

    //    // Clear user data from session
    //    //sessionManager.clearData(config.USER_COOKIE_KEY);

    //    logger.log('LogAuth', componentName, 'User logged out', {
    //        userId: user.userId || UNKNOWN_USER_ID
    //    });

    //}, []);

    /**
    * Logout function - enhanced to prevent re-authentication
    */
    const logout = useCallback(() => {
        // Clear auth promise cache first to prevent any pending operations
        if (authPromiseRef.current.promise) {
            authPromiseRef.current.promise = null;
            authPromiseRef.current.timestamp = 0;
        }

        // Call the internalAuthService logout
        internalAuthService.logout();

        // Reset state to default values immediately
        setUser(defaultUser);
        setIsAuthenticated(false);
        setIsAnonymous(true);
        setRequiresRelogin(false);
        setLostUserInfo(null);
        setError(null);
        setIsInitializing(false); // Important: don't leave this as true

        // Force version increment to ensure re-renders
        setAuthVersion(prev => prev + 1);

        logger.log('LogAuth', componentName, 'User logged out - state reset', {
            userId: user.userId || UNKNOWN_USER_ID
        });

    }, [user.userId]);

    /**
     * Convert an anonymous account to a permanent one
     * Leveraged from existing AuthProvider
     */
    const convertAnonymousAccount = useCallback(async (
        userId: number,
        newUserName: string,
        email: string,
        firstName: string,
        lastName: string,
        password: string,
        phone?: string,
        city?: string,
        state?: string,
        zipCode?: string,
        country?: string,
        address1?: string,
        address2?: string
    ) => {
        setIsInitializing(true);
        setError(null);

        //userId should not be 0


        try {
            const success = await internalAuthService.convertAnonymousAccount(
                userId,
                newUserName,
                email,
                firstName,
                lastName,
                password,
                phone,
                city,
                state,
                zipCode,
                country,
                address1,
                address2
            );

            if (success) {
                // Update user info
                const updatedUser = {
                    ...user,
                    userName: newUserName,
                    email,
                    isAnonymous: false,
                    firstName,
                    lastName
                };

                // Update state with converted user info
                updateUserState(updatedUser);
                setRequiresRelogin(false);

                logger.log('LogAuth', componentName, 'Anonymous account converted successfully', {
                    userId: updatedUser.userId
                });

            }

            return success;
        } catch (error) {
            logger.log('LogError', componentName, 'Account conversion error:', { userId: UNKNOWN_USER_ID, error });
            setError(error instanceof Error ? error.message : 'Failed to convert account');
            throw error;
        } finally {
            setIsInitializing(false);
        }
    }, [user, updateUserState]);

    /**
     * Check username availability
     * Leveraged from existing AuthProvider
     */
    const checkUsernameAvailability = useCallback(async (userName: string, userId: number = 0) => {
        try {
            return await internalAuthService.checkUsernameAvailability(userName, userId);
        } catch (error) {
            logger.log('LogError', componentName, 'Username availability check error:', { userId: UNKNOWN_USER_ID, error });
            setError(error instanceof Error ? error.message : 'Failed to check username availability');
            throw error;
        }
    }, []);

    /**
     * Clear relogin flag
     * Leveraged from existing AuthProvider
     */
    const clearReloginFlag = useCallback(() => {
        setRequiresRelogin(false);
        setLostUserInfo(null);
        sessionManager.clearData('lost_user_info');
    }, []);

    //// Periodic refresh interval
    //useEffect(() => {
    //    // Initialize authentication on mount
    //    initializeAuth().catch(err => {
    //        logger.log('LogError', componentName, 'Initial authentication failed:', err);
    //    });

    //    // Set up periodic refresh if interval is positive
    //    if (refreshInterval > 0) {
    //        const intervalId = setInterval(() => {
    //            // Only refresh if authenticated and not anonymous
    //            if (isAuthenticated && !isAnonymous) {
    //                logger.log('LogAuth', componentName, 'Scheduled refresh triggered');
    //                debouncedRefreshUserInfo();
    //            }
    //        }, refreshInterval);

    //        // Clean up interval on unmount
    //        return () => clearInterval(intervalId);
    //    }
    //}, [initializeAuth, debouncedRefreshUserInfo, refreshInterval, isAuthenticated, isAnonymous]);

    const hasInitialized = useRef(false);

    useEffect(() => {
        // Only initialize once on mount
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            initializeAuth().catch(err => {
                logger.log('LogError', componentName, 'Initial authentication failed:', err);
            });
        }

        // Set up periodic refresh if interval is positive
        if (refreshInterval > 0) {
            const intervalId = setInterval(() => {
                // Only refresh if authenticated and not anonymous
                if (isAuthenticated && !isAnonymous) {
                    logger.log('LogAuth', componentName, 'Scheduled refresh triggered');
                    debouncedRefreshUserInfo();
                }
            }, refreshInterval);

            // Clean up interval on unmount
            return () => clearInterval(intervalId);
        }
    }, [refreshInterval]); // Remove problematic dependencies

    //+++++++++++++++++++++++++++++++



    /**
 * Create an anonymous account
 */
    const createAnonymousAccount = useCallback(async (): Promise<UserInfo> => {
        try {
            setIsInitializing(true);
            const anonymousUser = await internalAuthService.createAnonymousAccount();
            updateUserState(anonymousUser);
            return anonymousUser;
        } catch (error) {
            logger.log('LogError', componentName, 'Error creating anonymous account:', { userId: UNKNOWN_USER_ID, error });
            setError(error instanceof Error ? error.message : 'Failed to create anonymous account');
            throw error;
        } finally {
            setIsInitializing(false);
        }
    }, [updateUserState]);


    ///**
    // * Ensure a quote group exists for the current user
    // */
    //const ensureQuoteGroup = useCallback(async (): Promise<number> => {
    //    try {
    //        return await internalAuthService.ensureQuoteGroup();
    //    } catch (error) {
    //        logger.log('LogError', componentName, 'Error ensuring quote group:', error);
    //        setError(error instanceof Error ? error.message : 'Failed to ensure quote group');
    //        throw error;
    //    }
    //}, []);



    //++++++++++++++++++++++++++++++++

    // Context value
    const contextValue: AuthManagerContextType = {
        user,
        isAuthenticated,
        isAnonymous,
        isInitializing,
        error,
        requiresRelogin,
        lostUserInfo,
        login,
        logout,
        convertAnonymousAccount,
        checkUsernameAvailability,
        refreshUserInfo,
        clearReloginFlag,
        createAnonymousAccount,
        saveUserInfoToCookie,
        ensureUser,
        lastRefreshTimestamp,
        refreshCount,
        authVersion
    };

    // Render the provider with context value
    return (
        <AuthManagerContext.Provider value={contextValue}>
            {children}
        </AuthManagerContext.Provider>
    );
};

/**
 * Hook to use authentication manager context
 */
export const useAuthManager = () => {
    const context = useContext(AuthManagerContext);

    if (!context) {
        throw new Error('useAuthManager must be used within an AuthenticationManager');
    }

    return context;
};

export default AuthenticationManager;